/**
 * WebSocket hook for real-time notification bell updates.
 *
 * Connects to: ws(s)://host/ws/notifications/?token=<token>
 * Backend consumer group: notif_{schema}_{user_id}
 *
 * Dispatches prependNotification when msg.type === "notification.new".
 * Auto-reconnects up to MAX_RECONNECT_ATTEMPTS on disconnect.
 * Cleans up socket on unmount.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { prependNotification, setWsConnected } from '../services/Redux/notificationReducer';

const RECONNECT_DELAY_MS = 3000;
const MAX_RECONNECT_ATTEMPTS = 5;

const selectToken = (state) => state.tokenReducer?.token;

const useNotificationSocket = () => {
  const dispatch = useDispatch();
  const wsRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef(null);
  const mountedRef = useRef(true);

  const token = useSelector(selectToken);

  const connect = useCallback(() => {
    if (!token) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (!mountedRef.current) return;

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsUrl = `${protocol}://${window.location.host}/ws/notifications/?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        dispatch(setWsConnected(true));
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'notification.new' && msg.notification) {
            dispatch(prependNotification(msg.notification));
          }
        } catch (err) {
          console.warn('[NotificationSocket] Failed to parse message:', err);
        }
      };

      ws.onerror = () => {
        dispatch(setWsConnected(false));
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        dispatch(setWsConnected(false));
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts.current += 1;
          reconnectTimer.current = setTimeout(connect, RECONNECT_DELAY_MS);
        }
      };
    } catch (err) {
      console.warn('[NotificationSocket] Connection failed:', err);
    }
  }, [token, dispatch]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);
};

export default useNotificationSocket;
